'use client';

import { mergeProps, mergeRefs, useObjectRef } from '@react-aria/utils';
import type { Context, ForwardedRef, RefObject } from 'react';
import { useMemo } from 'react';
import type { ContextValue, SlotProps } from 'react-aria-components';
import { useSlottedContext } from 'react-aria-components';

import { cn } from './cn';

/**
 * Re-implement `useContextProps` from "react-aria-components", wrapping the
 * className in `cn` to merge tailwind classes.
 *
 * @see https://github.com/adobe/react-spectrum/blob/main/packages/react-aria-components/src/utils.tsx
 * @see https://github.com/adobe/react-spectrum/blob/main/packages/%40react-spectrum/s2/src/useSpectrumContextProps.ts
 */
export function useContextProps<T extends object, U extends SlotProps, E>(
  props: T & SlotProps,
  ref: ForwardedRef<E>,
  context: Context<ContextValue<U, E>>
): [T, RefObject<E | null>] {
  const ctx = useSlottedContext(context, props.slot) || {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { ref: contextRef, ...contextProps } = ctx as any;
  const mergedRef = useObjectRef(
    useMemo(() => mergeRefs(ref, contextRef), [ref, contextRef])
  );
  const mergedProps = mergeProps(contextProps, props) as unknown as T;

  // wrap the className in `cn` to merge tailwind classes
  if ('className' in mergedProps && mergedProps.className) {
    mergedProps.className = cn(mergedProps.className);
  }

  if (
    'style' in contextProps &&
    contextProps.style &&
    'style' in props &&
    props.style
  ) {
    // @ts-expect-error — mergeProps does not merge `style`
    mergedProps.style = {
      ...contextProps.style,
      ...props.style,
    };
  }

  return [mergedProps, mergedRef];
}
